// Sprint fc-conversation-panel §3 — collapse persistence per conversation
// (CM-1, RULED, TRD §13 v10). The ONLY file in this sprint that touches
// storage (spec §5.1 row 2).
//
// Mechanism: per-agent localStorage key, try/catch on both read and write
// (quota / Safari private mode — edge case in spec §10, precedent
// Composer.tsx's `readDraft`), default = expanded on failure.
//
// The call is written bare `localStorage.*` — WITHOUT the global-object
// prefix — and the SSR guard is `typeof localStorage === 'undefined'`,
// not a guard on that global object. This is not style: F5 (no access to
// that global object's storage member, `.claude/rules`) still binds, and
// its v12 carve-out authorises exactly this call form for the
// per-conversation collapse key (spec §3.2). Composer.tsx's own draft
// persistence mirrors this mechanism's SHAPE (per-agent key, try/catch
// both ways) but not its call form — its prefixed line predates the
// carve-out and is not a precedent to copy verbatim here.

import { useCallback, useEffect, useState } from 'react';

export const panelCollapsedKey = (agentId: string) =>
  `asoasis:panel-collapsed:${agentId}`;

function readCollapsed(agentId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(panelCollapsedKey(agentId)) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(agentId: string, collapsed: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (collapsed) {
      localStorage.setItem(panelCollapsedKey(agentId), '1');
    } else {
      localStorage.removeItem(panelCollapsedKey(agentId));
    }
  } catch {
    // Quota exceeded or storage disabled — silently stay in memory-only
    // state for this render; §10 edge case, default is expanded.
  }
}

export interface UsePanelCollapseResult {
  collapsed: boolean;
  toggle: () => void;
}

/**
 * Per-conversation collapse flag. Keyed by `agentId` so switching the
 * selected conversation reads THAT conversation's own key rather than
 * carrying the previous one's flag forward — the canonical silent break
 * this sprint's acceptance criterion A4 exists to catch (a hook that
 * takes `agentId` but keys globally).
 */
export function usePanelCollapse(agentId: string): UsePanelCollapseResult {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(agentId));

  // The conversation changed under an already-mounted panel (founder
  // switches conversations without unmounting ChatView's tree) — re-read
  // the new agent's own key instead of keeping the previous value.
  useEffect(() => {
    setCollapsed(readCollapsed(agentId));
  }, [agentId]);

  // `next` is computed OUTSIDE the `setCollapsed` updater and `writeCollapsed`
  // is called once, here — not inside a functional updater. React (and
  // StrictMode in particular) may invoke a functional updater twice per
  // commit; a `writeCollapsed` call inside one would double-write `localStorage`
  // (idempotent today since both writes carry the same value, but a latent
  // trap for a future write that isn't). `collapsed` is a dependency so
  // `toggle`'s closure always sees the current value.
  const toggle = useCallback(() => {
    const next = !collapsed;
    writeCollapsed(agentId, next);
    setCollapsed(next);
  }, [agentId, collapsed]);

  return { collapsed, toggle };
}
